import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/*
 * The frame every sign-in page shares: a split screen. The dark half says what
 * Agents Anywhere does, as the loop the work runs through; the light half is
 * the form. Sign up and Log in sit on one toggle at the top of the form, so a
 * person who landed on the wrong one switches without hunting for a link.
 *
 * On a phone the dark half shrinks to a band with the logo and the line, and
 * the form follows it.
 *
 * The dark half has no scroller of its own: it is as tall as the window or its
 * content, whichever is taller, so a short window scrolls the page once rather
 * than showing a second scrollbar down the middle of the screen.
 */

export const FIELD = 'h-[48px] w-full rounded-[8px] border border-[#dedede] bg-white px-4 text-[15px] text-[#18191d] outline-none transition-colors placeholder:text-[#92949d] focus:border-[#18191d]';
export const LABEL = 'mb-2 block text-[14px] font-semibold text-[#18191d]';
export const PRIMARY = 'h-[48px] w-full rounded-[8px] bg-[#18191d] text-[15px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-50';
export const QUIET = 'inline-flex h-[48px] w-full items-center justify-center gap-3 rounded-[8px] border border-[#dedede] bg-white text-[15px] font-semibold text-[#18191d] transition-colors hover:border-[#18191d] disabled:opacity-50';
export const FOOTNOTE = 'mt-6 text-center text-[13px] leading-[1.55] text-[#666971]';
export const FOOTLINK = 'font-semibold text-[#18191d] underline underline-offset-4';

/** The line between Continue with Google and the email form. */
export const OrDivider: React.FC = () => (
  <div className="my-6 flex items-center gap-4 text-[12px] font-medium uppercase tracking-[0.08em] text-[#92949d]">
    <span className="h-px flex-1 bg-[#e7e7e4]" />
    or
    <span className="h-px flex-1 bg-[#e7e7e4]" />
  </div>
);

/** Google's mark, in Google's colours. */
export const GoogleMark: React.FC = () => (
  <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const EYE = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1.8 12S5.7 5.5 12 5.5 22.2 12 22.2 12 18.3 18.5 12 18.5 1.8 12 1.8 12Z" />
    <circle cx="12" cy="12" r="3.1" />
  </svg>
);

const EYE_OFF = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.7 6.2A10.8 10.8 0 0 1 12 6.1c6.3 0 10.2 6 10.2 6a19 19 0 0 1-3.3 3.8" />
    <path d="M6.6 7.7A18.6 18.6 0 0 0 1.8 12.1S5.7 18 12 18c1.3 0 2.5-.2 3.6-.6" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="M3.2 3.2 20.8 20.8" />
  </svg>
);

/** A password field that can be read back, because a typed password nobody can
 *  see is the most common reason a correct password is entered wrong twice. */
export const PasswordField: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  autoComplete: string;
  autoFocus?: boolean;
  testId?: string;
  /** Sits on the label line, for example the Forgot it? link. */
  trailing?: React.ReactNode;
  /** The label and field look of a host with its own style. */
  labelClassName?: string;
  inputClassName?: string;
}> = ({ id, label, value, onChange, placeholder, autoComplete, autoFocus, testId, trailing, labelClassName, inputClassName }) => {
  const [shown, setShown] = useState(false);
  let type = 'password';
  let action = 'Show password';
  let icon = EYE;
  if (shown) {
    type = 'text';
    action = 'Hide password';
    icon = EYE_OFF;
  }
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className={`${labelClassName || LABEL} mb-0`}>{label}</label>
        {trailing}
      </div>
      <div className="relative">
        <input
          id={id}
          data-testid={testId}
          type={type}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required
          autoFocus={autoFocus}
          className={`${inputClassName || FIELD} pr-12`}
        />
        <button
          type="button"
          onClick={() => setShown(!shown)}
          aria-label={action}
          aria-pressed={shown}
          title={action}
          className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-[#777982] transition-colors hover:text-[#18191d]"
        >
          {icon}
        </button>
      </div>
    </div>
  );
};

/* The loop every piece of work runs through, from the first conversation to
   the release, and then round again with the next opportunity. */
const LOOP = [
  { name: 'Discover', line: 'Your company, people, tools and repeated work' },
  { name: 'Clarity', line: 'The right kind of solution for each problem' },
  { name: 'Build', line: 'Agents build it. Your expert steers.' },
  { name: 'Review', line: 'Every flow checked before you accept it' },
  { name: 'Deliver', line: 'Released safely, then live and measured' },
];

const LoopSteps: React.FC = () => (
  <div>
    <div className="relative pl-11">
      {/* The way back from Deliver to Discover. */}
      <span aria-hidden="true" className="absolute bottom-[14px] left-0 top-[14px] w-[26px] rounded-l-[6px] border-y border-l border-dashed border-white/30" />
      <svg aria-hidden="true" className="absolute left-[22px] top-[9px] h-[10px] w-[10px] text-white/50" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 1.5 7 5 3 8.5" /></svg>
      <ol className="m-0 list-none p-0">
        {LOOP.map((step, i) => {
          const first = i === 0;
          const last = i === LOOP.length - 1;
          return (
            <li key={step.name} className="relative flex gap-5 pb-7 last:pb-0">
              {!last && <span aria-hidden="true" className="absolute left-[8px] top-[22px] bottom-[4px] w-px bg-white/20" />}
              <span
                aria-hidden="true"
                className={first
                  ? 'relative mt-[3px] h-[17px] w-[17px] shrink-0 rounded-full bg-accent-500 shadow-[0_0_0_5px_rgba(255,234,0,0.16)]'
                  : 'relative mt-[3px] h-[17px] w-[17px] shrink-0 rounded-full border-[1.5px] border-white/45 bg-ink'}
              />
              <div className="min-w-0">
                <div className="font-code text-[12.5px] font-semibold uppercase tracking-[0.14em]">
                  <span className="text-white/45">{String(i + 1).padStart(2, '0')}</span>
                  <span className={first ? 'ml-3 text-accent-500' : 'ml-3 text-white'}>{step.name}</span>
                </div>
                <p className="m-0 mt-1 text-[14.5px] leading-[1.45] text-white/75">{step.line}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
    <p className="m-0 mt-8 flex items-center gap-2 font-code text-[11.5px] font-semibold uppercase tracking-[0.14em] text-white/45">
      <svg aria-hidden="true" className="h-[14px] w-[14px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>
      Then back to Discover, with the next opportunity
    </p>
  </div>
);

/** Sign up and Log in on one toggle. Each is its own address, so a link to
 *  either still lands on it; the toggle only moves between them. */
const AuthTabs: React.FC<{ tab: 'signup' | 'login' }> = ({ tab }) => {
  const base = 'flex h-[44px] flex-1 items-center justify-center rounded-full text-[15px] font-semibold transition-colors';
  const on = `${base} bg-white text-[#18191d] shadow-[0_1px_3px_rgba(24,25,29,0.12)]`;
  const off = `${base} text-[#666971] hover:text-[#18191d]`;
  return (
    <nav aria-label="Sign up or log in" className="mb-9 flex rounded-full bg-[#f1f1ef] p-1">
      <Link to="/signup" aria-current={tab === 'signup' ? 'page' : undefined} className={tab === 'signup' ? on : off} data-testid="auth-tab-signup">Sign up</Link>
      <Link to="/login" aria-current={tab === 'login' ? 'page' : undefined} className={tab === 'login' ? on : off} data-testid="auth-tab-login">Log in</Link>
    </nav>
  );
};

export const AuthShell: React.FC<{
  title: React.ReactNode;
  sub?: React.ReactNode;
  /** Sign up and Log in show the toggle between them; the other pages do not. */
  tab?: 'signup' | 'login';
  children: React.ReactNode;
}> = ({ title, sub, tab, children }) => (
  <div className="min-h-screen bg-white lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
    <aside className="flex flex-col bg-ink px-6 py-8 text-white sm:px-10 lg:min-h-screen lg:px-14 lg:py-12 xl:px-20">
      <Link to="/" className="inline-flex"><img src="/logo-white.png" alt="Agents Anywhere" className="h-7 w-auto object-contain lg:h-8" /></Link>
      <div className="mt-8 lg:my-auto lg:py-12">
        <h2 className="m-0 text-[30px] font-bold leading-[1.08] tracking-[-0.025em] sm:text-[38px] lg:text-[42px] xl:text-[50px] 2xl:text-[58px]">Problem in.<br />The right solution out.</h2>
        <p className="m-0 mt-5 max-w-[34em] text-[15px] leading-[1.6] text-white/70 lg:mt-7 lg:text-[17px]">We discover what should get better, bring clarity on the right kind of solution, then build, review and deliver it. Your expert stays in the loop.</p>
        <div className="mt-14 hidden lg:block"><LoopSteps /></div>
      </div>
    </aside>
    <main className="flex justify-center px-6 py-10 sm:px-10 lg:items-center lg:py-14">
      <div className="w-full max-w-[460px]">
        {tab && <AuthTabs tab={tab} />}
        <h1 className="m-0 text-[30px] font-bold leading-[1.1] tracking-[-0.02em] text-[#18191d] sm:text-[34px]">{title}</h1>
        {sub && <p className="m-0 mt-3 text-[15px] leading-[1.55] text-[#666971]">{sub}</p>}
        <div className="mt-8">{children}</div>
      </div>
    </main>
  </div>
);

export default AuthShell;
